import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { Trophy, Users, CheckSquare, Timer, MessageSquareText } from 'lucide-react';

// Detective Dino SVG component - matches the header icon exactly
const DetectiveDinoIcon = ({ className = "w-6 h-6" }) => {
  const pixels = [
    [0,0,0,0,0,0,1,1,1,1,1,1],
    [0,0,0,0,0,1,1,1,1,1,1,1,1],
    [0,0,0,0,0,1,1,2,1,1,1,1,1],
    [0,0,0,0,0,1,1,1,1,1,1,1],
    [0,0,0,0,0,1,1,1,1,0,0,0],
    [0,0,0,1,0,1,1,1,1,1,0,0],
    [0,0,1,1,1,1,1,1,1,1,0,0],
    [0,1,1,1,1,1,1,1,1,0,0,0],
    [1,1,1,1,1,1,1,1,0,0,0,0],
    [0,1,1,1,1,1,1,1,0,0,0,0],
    [0,0,1,1,1,1,1,0,0,0,0,0],
    [0,0,0,1,1,0,1,1,0,0,0,0],
    [0,0,0,1,0,0,0,1,0,0,0,0],
    [0,0,0,1,1,0,0,0,0,0,0,0],
  ];
  const fills = { 1: 'currentColor', 2: '#FFFFFF' };
  
  return (
    <svg className={className} viewBox="-2 4 64 52" fill="none" xmlns="http://www.w3.org/2000/svg">
      {pixels.map((row, y) => 
        row.map((pixel, x) => 
          pixel > 0 && (
            <rect key={`${x}-${y}`} x={x * 4} y={y * 4} width="4" height="4" fill={fills[pixel]} />
          )
        )
      )}
      <line x1="40" y1="28" x2="46" y2="32" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
      <circle cx="52" cy="26" r="5" fill="none" stroke="#8B5CF6" strokeWidth="2"/>
      <circle cx="52" cy="26" r="3" fill="#8B5CF6" fillOpacity="0.15"/>
      <line x1="48" y1="30" x2="44" y2="34" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="6" cy="10" r="4.5" fill="#8B5CF6"/>
      <circle cx="13" cy="11" r="1.5" fill="#8B5CF6"/>
      <circle cx="17" cy="13" r="1" fill="#8B5CF6"/>
      <text x="6" y="12.5" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">?</text>
    </svg>
  );
};

// Purple Llama icon for Timer tab - matches DinoLlamaLoader LlamaSprite exactly
const PurpleLlamaIcon = ({ className = "w-5 h-5" }) => {
  const pixels = [
    [0,0,0,1,0,1,0,0],
    [0,0,0,1,1,1,0,0],
    [0,0,0,0,1,0,0,0],
    [0,0,0,0,1,0,0,0],
    [0,0,0,0,1,0,0,0],
    [0,0,1,1,1,1,0,0],
    [0,1,1,1,1,1,1,0],
    [0,1,1,1,1,1,1,0],
    [0,0,1,1,1,1,0,0],
    [0,0,1,0,0,1,0,0],
    [0,0,1,0,0,1,0,0],
    [0,0,1,0,0,1,0,0],
  ];
  return (
    <svg className={className} viewBox="0 0 32 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      {pixels.map((row, y) =>
        row.map((pixel, x) =>
          pixel === 1 && (
            <rect key={`${x}-${y}`} x={x * 4} y={y * 4} width="4" height="4" fill="#7C3AED" />
          )
        )
      )}
    </svg>
  );
};

export const BottomNav = () => {
  const { isSpanish } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  
  const tabs = [
    {
      path: '/library',
      icon: DetectiveDinoIcon,
      shortLabel: isSpanish ? 'Frases' : 'Library',
      requiresAuth: false,
    },
    {
      path: '/todo',
      icon: CheckSquare,
      shortLabel: isSpanish ? 'Tareas' : 'To-Do',
      requiresAuth: false,
    },
    {
      path: '/timer',
      icon: PurpleLlamaIcon,
      shortLabel: isSpanish ? 'Temporizador' : 'Timer',
      requiresAuth: false,
    },
    {
      path: '/interpreter',
      icon: MessageSquareText,
      shortLabel: isSpanish ? 'Intérprete' : 'Interpret',
      requiresAuth: false,
    },
    {
      path: '/friends',
      icon: Users,
      shortLabel: isSpanish ? 'Amigos' : 'Friends',
      requiresAuth: true,
    },
  ];

  const handleTabClick = (e, tab) => {
    if (tab.requiresAuth && !user) {
      e.preventDefault();
      e.stopPropagation();
      window.location.href = '/login';
    }
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-t border-border" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="max-w-3xl mx-auto px-0.5 sm:px-2">
        <div className="flex justify-between items-center h-14">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = location.pathname === tab.path || 
              (tab.path === '/library' && location.pathname === '/');
            
            return (
              <NavLink
                key={tab.path}
                to={tab.requiresAuth && !user ? '#' : tab.path}
                onClick={(e) => handleTabClick(e, tab)}
                className={`flex flex-col items-center justify-center flex-1 py-1 transition-colors ${
                  isActive 
                    ? 'text-[#00A896] dark:text-[#00E5CC]' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`nav-tab-${tab.path.replace('/', '')}`}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-[#00A896] dark:text-[#00E5CC]' : ''}`} />
                <span className={`text-[9px] mt-0.5 font-medium text-center leading-tight truncate w-full px-0.5 ${
                  isActive ? 'text-[#00A896] dark:text-[#00E5CC]' : ''
                }`}>
                  {tab.shortLabel}
                </span>
              </NavLink>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default BottomNav;
